import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { syncAirtableStatus } from '../services/patient';

function normalizeStatus(raw) {
  if (!raw) return 'unknown';
  const s = String(raw).toLowerCase();
  if (['running', 'in_progress', 'syncing', 'processing', 'pending', 'progress', 'started', 'retry'].includes(s)) return 'running';
  if (['done', 'completed', 'success', 'succeeded', 'finished'].includes(s)) return 'completed';
  if (['error', 'failed', 'failure'].includes(s)) return 'failed';
  if (['idle', 'not_running', 'stopped'].includes(s)) return 'idle';
  return s;
}

function toProgressPercent(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const direct =
    payload.progress ??
    payload.progress_percent ??
    payload.progress_percentage ??
    payload.percentage ??
    payload.percent;
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return Math.max(0, Math.min(100, direct));
  }

  const current = payload.current ?? payload.processed ?? payload.completed ?? payload.done ?? payload.records_processed;
  const total = payload.total ?? payload.total_records ?? payload.records_total;
  if (
    typeof current === 'number' &&
    typeof total === 'number' &&
    Number.isFinite(current) &&
    Number.isFinite(total) &&
    total > 0
  ) {
    return Math.max(0, Math.min(100, (current / total) * 100));
  }

  return null;
}

export default function useAirtableSyncStatus({ enabled, pollIntervalMs = 1500, taskId } = {}) {
  const [statusPayload, setStatusPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedOnceRef = useRef(false);
  const status = useMemo(() => normalizeStatus(statusPayload?.status ?? statusPayload?.state), [statusPayload]);
  const progress = useMemo(() => toProgressPercent(statusPayload), [statusPayload]);
  const isRunning = status === 'running';
  const shouldStopPolling =
    status === 'completed' ||
    status === 'failed' ||
    statusPayload?.result != null ||
    (typeof progress === 'number' && progress >= 100);

  const refresh = useCallback(async () => {
    try {
      if (!hasLoadedOnceRef.current) setLoading(true);
      setError(null);
      if (!taskId) return;
      const data = await syncAirtableStatus(taskId);
      setStatusPayload(data);
      hasLoadedOnceRef.current = true;
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (!enabled || !taskId) return;
    hasLoadedOnceRef.current = false;
    setStatusPayload(null);
    refresh();
    if (shouldStopPolling) return;
    const id = setInterval(refresh, pollIntervalMs);
    return () => clearInterval(id);
  }, [enabled, pollIntervalMs, refresh, taskId, shouldStopPolling]);

  return { statusPayload, status, progress, isRunning, loading, error, refresh };
}

